<template>
    <Head :title="pageTitle" />
    <BaseLayout width="narrow" gap="md" fill-height role="main" class="min-h-dvh" content-class="items-center justify-center text-ink">
        <TeamInvitationContinuation v-if="unavailable" :user="userDetails" :teams-url="teams_url" landing />
        <template v-else>
            <p class="title-card-serif">{{ t("teamJoin.brand") }}</p>
            <BaseCard as="section" padding="none" class="w-full max-w-lg" aria-labelledby="team-join-title">
                <div class="flex flex-col items-center gap-6 p-6 text-center md:p-10">
                    <div class="flex size-12 items-center justify-center rounded-full bg-surface-sunken text-ink-muted" aria-hidden="true">
                        <i class="bi bi-people title-section"></i>
                    </div>
                    <div class="flex flex-col gap-4">
                        <h1 id="team-join-title" class="display-serif">{{ pageTitle }}</h1>
                        <p v-if="storageUnavailable" role="alert" class="body text-ink-muted">{{ t("teamJoin.storageUnavailable") }}</p>
                        <p v-else-if="!hasInvitation" class="body text-ink-muted">{{ t("teamJoin.invalidLink") }}</p>
                        <p v-else-if="!authenticated" class="body text-ink-muted">{{ t("teamJoin.description") }}</p>
                        <p v-else-if="requiresOnboarding" class="body text-ink-muted">{{ t("teamJoin.onboarding") }}</p>
                    </div>
                    <template v-if="hasInvitation && !storageUnavailable">
                        <BaseLink v-if="!authenticated" :href="login_url" class="base-btn-primary flex w-full items-center justify-center gap-2">
                            {{ t("teamJoin.signIn") }}
                            <ArrowRight class="size-5" aria-hidden="true" />
                        </BaseLink>
                        <BaseLink v-else-if="requiresOnboarding" :href="onboarding_url" class="base-btn-primary flex w-full items-center justify-center gap-2">
                            {{ t("teamJoin.continueSetup") }}
                            <ArrowRight class="size-5" aria-hidden="true" />
                        </BaseLink>
                        <TeamInvitationContinuation v-else :user="userDetails" :teams-url="teams_url" landing @onboarding-required="requiresOnboarding = true" />
                    </template>
                </div>
            </BaseCard>
        </template>
    </BaseLayout>
</template>

<script setup lang="ts">
import { Head } from "@inertiajs/vue3";
import type { UserDetails } from "~vue/appTypes";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseLayout from "~vue/components/BaseLayout.vue";
import ArrowRight from "~vue/icons/ArrowRight.vue";
import BaseLink from "~vue/router/BaseLink.vue";
import TeamInvitationContinuation from "~vue/Teams/TeamInvitationContinuation.vue";
import { pendingTeamJoin, teamJoinStorageFailed } from "~vue/Teams/teamJoinContinuation";
import type { Theme } from "~vue/utils/theme";
import { computed, inject, onBeforeUnmount, type Ref, ref } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps<{ authenticated: boolean; has_onboarded: boolean; login_url: string; onboarding_url: string; teams_url: string; return_theme: Theme }>();
const { t } = useI18n();
const userDetails = inject<Ref<UserDetails>>("user_details");
const unavailable = computed(() => props.authenticated && userDetails?.value.is_teams_enabled === false);
const hasInvitation = pendingTeamJoin() !== null;
const requiresOnboarding = ref(!props.has_onboarded);
const storageUnavailable = teamJoinStorageFailed();
const pageTitle = computed(() =>
    unavailable.value ? t("teamJoin.brand") : storageUnavailable ? t("teamJoin.storageTitle") : !hasInvitation ? t("teamJoin.invalidTitle") : t("teamJoin.title"),
);

globalThis.document.documentElement.dataset.theme = "tonal";
onBeforeUnmount(() => {
    globalThis.document.documentElement.dataset.theme = props.return_theme;
});
</script>
